import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle, Clock, Shield, CreditCard, ArrowRight, Code } from 'lucide-react';
import { Seo, withTemplate, canonicalize } from '@/lib/seo';
import { breadcrumbJsonLd } from '@/lib/structuredData';
import JsonLD from '@/components/JsonLD';
import { shouldShowDevDiscount } from '@/lib/flags';

export default function Lease() {
  const [showDevDiscount, setShowDevDiscount] = useState(false);

  useEffect(() => {
    shouldShowDevDiscount().then(setShowDevDiscount);
  }, []);

  return (
    <>
      <Seo
        title={withTemplate('Lease (Use Monthly): How it works')}
        description="Start using your domain while you pay monthly. Cancel anytime. Buy at BIN whenever you're ready."
        canonical={canonicalize('/lease')}
        og={{
          title: 'Lease (Use Monthly): How it works',
          description: 'Use now, pay monthly. Cancel anytime or buy at BIN.',
          url: canonicalize('/lease'),
        }}
        twitter={{
          card: 'summary',
          title: 'Lease (Use Monthly): How it works',
          description: 'Use now, pay monthly. Cancel anytime or buy at BIN.',
        }}
      />

      <JsonLD
        data={breadcrumbJsonLd([
          { name: 'Home', url: canonicalize('/') },
          { name: 'Lease', url: canonicalize('/lease') },
        ])}
      />

      <div className="min-h-screen bg-gradient-to-br from-background to-muted/20 py-12 px-4">
        <div className="max-w-4xl mx-auto space-y-8">
          {/* Header */}
          <div className="text-center space-y-4">
            <h1 className="text-4xl lg:text-5xl font-bold text-foreground">
              Lease (Use Monthly): How it works
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Get the domain you want now, pay monthly, and own it completely by exercising your
              option to buy at BIN anytime.
            </p>
          </div>

          {/* Steps */}
          <Card className="apple-card">
            <CardHeader>
              <CardTitle className="text-2xl font-semibold">The Process</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-6">
                {[
                  {
                    step: 1,
                    title: 'Choose a domain',
                    description:
                      'Browse our inventory and find the perfect domain for your business.',
                    icon: <CheckCircle className="w-5 h-5" />,
                  },
                  {
                    step: 2,
                    title: 'Start monthly lease',
                    description:
                      'Select your lease plan and make your first monthly payment to begin.',
                    icon: <CreditCard className="w-5 h-5" />,
                  },
                  {
                    step: 3,
                    title: 'Instant DNS control',
                    description:
                      'Get full DNS and hosting control immediately - build your business right away.',
                    icon: <Shield className="w-5 h-5" />,
                  },
                  {
                    step: 4,
                    title: 'Use the domain while leasing',
                    description:
                      'Complete control over the domain while you decide if you want to buy it.',
                    icon: <ArrowRight className="w-5 h-5" />,
                  },
                  {
                    step: 5,
                    title: 'Buy at BIN anytime (optional)',
                    description:
                      "Exercise your option to buy the domain at the original BIN price whenever you're ready.",
                    icon: <CheckCircle className="w-5 h-5" />,
                  },
                ].map((item) => (
                  <div
                    key={item.step}
                    className="flex items-start gap-4 p-4 bg-muted/30 rounded-lg"
                  >
                    <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0 text-primary">
                      {item.icon}
                    </div>
                    <div>
                      <h3 className="font-semibold text-foreground mb-1">
                        Step {item.step}: {item.title}
                      </h3>
                      <p className="text-sm text-muted-foreground">{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* 80/20 Apply Link */}
          <div className="text-center">
            <Link
              to="/operate"
              className="text-xs text-muted-foreground hover:text-primary transition-colors"
            >
              Apply to operate (80/20) →
            </Link>
          </div>

          {/* Timeline & Fees */}
          <div className="grid md:grid-cols-2 gap-6">
            <Card className="apple-card">
              <CardHeader>
                <CardTitle className="text-xl font-semibold flex items-center gap-2">
                  <Clock className="w-5 h-5" />
                  Timeline
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  Start using your domain within hours of signing up. Monthly billing, cancel
                  anytime. Exercise your option to buy at the original BIN price whenever you decide
                  you want to own it.
                </p>
              </CardContent>
            </Card>

            <Card className="apple-card">
              <CardHeader>
                <CardTitle className="text-xl font-semibold flex items-center gap-2">
                  <Shield className="w-5 h-5" />
                  Fees
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  Simple monthly subscription pricing. No setup fees, no cancellation penalties. If
                  you buy the domain, we handle the transfer at no extra cost.
                </p>
              </CardContent>
            </Card>
          </div>

          {/* FAQ */}
          <Card className="apple-card">
            <CardHeader>
              <CardTitle className="text-2xl font-semibold">Frequently Asked Questions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <h3 className="font-semibold text-foreground mb-2">
                  Who owns the domain during lease?
                </h3>
                <p className="text-sm text-muted-foreground">
                  We retain ownership while you have exclusive usage rights and an option to buy at
                  the BIN price. You get complete DNS control and can build your business on it
                  immediately.
                </p>
              </div>

              <div>
                <h3 className="font-semibold text-foreground mb-2">
                  Do I get DNS and hosting control?
                </h3>
                <p className="text-sm text-muted-foreground">
                  Yes! You get complete DNS management and hosting control from day one. Build your
                  website, set up email, and use the domain just like you own it.
                </p>
              </div>

              <div>
                <h3 className="font-semibold text-foreground mb-2">Can I cancel anytime?</h3>
                <p className="text-sm text-muted-foreground">
                  Absolutely. Cancel your monthly subscription anytime with no penalties. You'll
                  retain access until the end of your current billing period.
                </p>
              </div>

              <div>
                <h3 className="font-semibold text-foreground mb-2">
                  How does the buy option work?
                </h3>
                <p className="text-sm text-muted-foreground">
                  You can exercise your option to buy the domain at the original BIN price anytime
                  during your lease. Previous lease payments do not count toward the purchase price.
                </p>
              </div>

              <div>
                <h3 className="font-semibold text-foreground mb-2">
                  What if someone else wants to buy it?
                </h3>
                <p className="text-sm text-muted-foreground">
                  As our lessee, you have the first right to buy at BIN price. We'll notify you if
                  we receive a purchase offer and give you 48 hours to exercise your option.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Dev Discount Block */}
          {showDevDiscount && (
            <Card className="apple-card border-primary/20 bg-gradient-to-br from-primary/5 to-primary/10">
              <CardHeader>
                <CardTitle className="text-xl font-semibold flex items-center gap-2">
                  <Code className="w-5 h-5 text-primary" />
                  Developer Discount
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-foreground">
                  Building something technical? Get <strong>20% off</strong> your first 3 months
                  when you lease a domain.
                </p>
                <p className="text-sm text-muted-foreground">
                  Valid for developers, startups, and technical teams. Mention code{' '}
                  <code className="bg-muted px-2 py-0.5 rounded">DEV20</code> when you contact me.
                </p>
                <Button asChild size="sm" variant="outline" className="mt-2">
                  <Link to="/contact?ref=dev-discount">Claim Discount</Link>
                </Button>
              </CardContent>
            </Card>
          )}

          {/* CTAs */}
          <Card className="apple-card">
            <CardContent className="text-center py-8">
              <h2 className="text-2xl font-semibold mb-4">Ready to get started?</h2>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button asChild size="lg" className="px-8 py-3">
                  <Link to="/domains">Browse Domains</Link>
                </Button>
                <Button asChild variant="outline" size="lg" className="px-8 py-3">
                  <Link to="/contact">Contact Me</Link>
                </Button>
              </div>
              <p className="text-sm text-muted-foreground mt-4">
                <Link to="/domains" className="hover:text-primary transition-colors">
                  Browse all domains
                </Link>
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  );
}
